import React from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';

const LEGAL_BASIS = [
  'The South African Constitution',
  'The Companies Act (NPC registration)',
  'The Basic Conditions of Employment Act',
  'The Labour Relations Act (support and fairness rights)',
  'CCMA Rules (assistance, preparation and accompaniment allowed)',
  'Freedom of Association (Section 18 of the Constitution)',
];

const STEPS = [
  {
    num: '1',
    title: 'Case Review',
    desc: 'Every case begins with clarity, truth and worker understanding.',
    points: [
      "Listening to the worker's full version",
      'Studying documents, warnings, contracts, messages and records',
      'Identifying labour rights violations',
      'Explaining legal processes in simple language',
      'Preparing a structured action plan',
    ],
  },
  {
    num: '2',
    title: 'Written & Telephonic Communication',
    desc: 'As a legally registered NPC and worker-support organisation, we may lawfully communicate with employers and institutions on a worker\'s behalf.',
    points: [
      'Send written correspondence to employers, CCMA, unions, UIF, or relevant institutions',
      'Request information, clarity, documents and explanations',
      'Raise concerns on behalf of a worker in a non-adversarial manner',
      'Record unfair treatment and procedural failures',
      'Conduct telephonic follow-ups where written communication is ignored, delayed or unacknowledged — strictly for administrative and support purposes',
    ],
  },
  {
    num: '3',
    title: 'Workplace Intervention & Accompaniment',
    desc: 'When a worker is called to a disciplinary hearing, grievance meeting, investigation, or consultation, we may accompany them as a support person, not as a representative.',
    points: [
      'If denied entry, we may wait outside',
      'Advise during breaks',
      'Help prepare responses',
      'Document procedural unfairness',
      'Assist with immediate escalation after the meeting',
    ],
  },
  {
    num: '4',
    title: 'Assistance with CCMA & Statutory Processes',
    desc: 'The law allows workers to receive support and assistance from organisations of their choice.',
    points: [
      'Drafting CCMA referral forms',
      'Preparing written statements',
      'Uploading and submitting documents',
      'Understanding timelines and procedures',
      'Preparing evidence and case summaries',
    ],
  },
  {
    num: '5',
    title: 'Coaching, Preparation & Confidence Support',
    desc: 'Especially important for shy, fearful or vulnerable workers, before hearings or CCMA sessions.',
    points: [
      'One-on-one coaching',
      'Process explanations',
      'Script preparation',
      'Confidence and clarity support',
      'Understanding of rights and expectations',
    ],
  },
  {
    num: '6',
    title: 'Accompaniment at CCMA & Institutions',
    desc: 'Even where we cannot speak inside the room, our presence levels the playing field.',
    points: [
      'Moral and emotional support',
      'Guidance during breaks',
      'Assistance with on-site preparation',
      'Protection against intimidation or manipulation',
    ],
  },
];

const ASSURANCES = [
  'Lawful support',
  'Clear communication',
  'Ethical boundaries',
  'Protection from intimidation',
  'Continuous guidance until case conclusion',
];

export default function HowWeWork() {
  return (
    <div>
      <PageHeader
        eyebrow="Full Organisational Statement"
        title="How We Work"
        subtitle="What Singumkhonto Wabasebenzi (The Spear of Workers) is, how we operate lawfully, and the support process every worker can expect."
      />

      {/* What We Are */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <h2>What The Spear of Workers Is</h2>
          <p>
            The Spear of Workers is a Non-Profit Company (NPC) legally registered in terms
            of South African company law, created to support, protect, guide, and empower
            workers, and to assist workers with workplace disputes through lawful support
            services.
          </p>
          <p>
            <strong>We are not a trade union and not a law firm.</strong> We do not
            litigate, issue legal demands, or represent workers as attorneys. Our strength
            lies in support, preparation, accompaniment, education, and procedural
            protection.
          </p>

          <h3 style={{ marginTop: 32 }}>We Operate Legally Under</h3>
          <ul>
            {LEGAL_BASIS.map((item) => (
              <li key={item} style={{ marginBottom: 8 }}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Support Process Steps */}
      <section className="section" style={{ background: '#f7f5ef' }}>
        <div className="container">
          <span className="eyebrow">What We Actually Do</span>
          <h2 style={{ marginTop: 12, marginBottom: 32 }}>Our Full Support Process</h2>

          <div className="grid grid-2">
            {STEPS.map((step) => (
              <div className="card" key={step.num}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 8 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--color-gold)' }}>
                    {step.num}
                  </span>
                  <h3 style={{ margin: 0 }}>{step.title}</h3>
                </div>
                <p style={{ fontStyle: 'italic', marginBottom: 14 }}>{step.desc}</p>
                <ul>
                  {step.points.map((p, i) => (
                    <li key={i} style={{ marginBottom: 6, fontSize: '0.92rem' }}>{p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Boundaries of telephonic communication - highlighted */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820 }}>
          <div className="card" style={{ borderLeft: '4px solid var(--color-gold)' }}>
            <h3>Boundaries of Telephonic Communication</h3>
            <p>During all calls, The Spear of Workers:</p>
            <ul>
              <li>Does not issue legal threats</li>
              <li>Does not make legal demands</li>
              <li>Does not negotiate settlements</li>
              <li>Does not claim authority to enforce compliance</li>
            </ul>
            <p style={{ marginBottom: 0 }}>
              All telephonic communication is administrative, supportive and procedural,
              and where necessary is followed by written confirmation to maintain a proper
              record.
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="dark-section section">
        <div className="container" style={{ maxWidth: 820 }}>
          <span className="eyebrow">Important Disclaimer</span>
          <h2 style={{ marginTop: 12 }}>On Communication & Support</h2>
          <p>The Spear of Workers NPC:</p>
          <ul>
            <li>Is not a trade union or legal practice</li>
            <li>Does not litigate or represent workers as attorneys</li>
            <li>Does not issue binding legal demands</li>
          </ul>
          <p>
            All communication — written or telephonic — is conducted solely to support,
            guide, protect and assist workers, and all final decisions rest with statutory
            bodies such as the CCMA, Labour Court, SAPS or relevant authorities.
          </p>
        </div>
      </section>

      {/* Final Assurance */}
      <section className="section">
        <div className="container" style={{ maxWidth: 820, textAlign: 'center' }}>
          <span className="eyebrow" style={{ justifyContent: 'center' }}>Final Assurance</span>
          <h2 style={{ marginTop: 12, marginBottom: 32 }}>Workers Who Engage With Us Are Guaranteed</h2>

          <div className="grid grid-3" style={{ textAlign: 'left' }}>
            {ASSURANCES.map((a) => (
              <div className="card" key={a} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ color: 'var(--color-gold)', fontSize: '1.2rem' }}>✔</span>
                <span style={{ fontWeight: 600 }}>{a}</span>
              </div>
            ))}
          </div>

          <p style={{ fontStyle: 'italic', marginTop: 40, fontSize: '1.1rem' }}>
            "We raise the spear for justice, dignity and worker empowerment — never
            outside the law."
          </p>

          <div style={{ marginTop: 24 }}>
            <Link to="/register" className="btn btn-gold">Become a Member</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
